import { useQuery } from '@tanstack/react-query';
import { Role } from '@pillar-portal/database/dist/frontend';
import {
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
    type ActiveRoleAssignmentResponse,
    type ListUpcomingUserAgreementsResponse,
    type UpcomingUserAgreement,
} from '@pillar-portal/api-types';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';
import { legalSurfacingApi } from '@/lib/pillarApi';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { useAuthStore } from '@/store/auth';

export function upcomingUserAgreementMessage(agreement: UpcomingUserAgreement): string {
    const takesEffect = formatTimestampForMerchantUi(
        agreement.takes_effect_ts,
        SYSTEM_DISPLAY_TIME_ZONE
    );
    return `An updated ${agreement.legal_document_title} takes effect on ${takesEffect}. You will be asked to accept it then.`;
}

/** The advance notices, without the queries that feed them. */
export function UpcomingUserAgreementsNotice({
    upcoming,
}: {
    readonly upcoming: readonly UpcomingUserAgreement[];
}) {
    if (upcoming.length === 0) {
        return null;
    }
    return (
        <div
            role="status"
            className="space-y-1 border-b border-sky-200 bg-sky-50 px-8 py-3 text-sm text-sky-900"
        >
            {upcoming.map((agreement) => (
                <p key={agreement.legal_document_uuid}>{upcomingUserAgreementMessage(agreement)}</p>
            ))}
        </div>
    );
}

/**
 * A non-blocking notice of user agreements assigned with a later start.
 * From that start onward, acceptance gates access as usual.
 */
export function UpcomingUserAgreementsBanner() {
    const user = useAuthStore((state) => state.user);
    // A session awaiting a password change may read only its profile; any
    // other request is refused with 401 and would sign the user out.
    const requiresPasswordChange: boolean = user?.requires_password_change === true;
    const { data: activeRoleAssignmentResponse } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await sessionSensitiveAuthApi.getActiveRoleAssignment(),
        enabled: !!user && !requiresPasswordChange,
        staleTime: 30000,
    });
    const activeRoleAssignment = getActiveRoleAssignmentOperationalResponse(
        activeRoleAssignmentResponse
    );
    const roleAssignmentUuid =
        activeRoleAssignment !== null &&
        (activeRoleAssignment.role === Role.PRINCIPAL ||
            activeRoleAssignment.role === Role.ADMIN ||
            activeRoleAssignment.role === Role.REP)
            ? activeRoleAssignment.role_assignment_uuid
            : null;
    const { data } = useQuery<ListUpcomingUserAgreementsResponse>({
        queryKey: ['legalSurfacing', 'upcomingUserAgreements', roleAssignmentUuid],
        queryFn: async () => await legalSurfacingApi.listUpcomingUserAgreements(),
        enabled: roleAssignmentUuid !== null,
        staleTime: 300000,
    });
    if (roleAssignmentUuid === null || data === undefined) {
        return null;
    }
    return <UpcomingUserAgreementsNotice upcoming={data.upcoming_user_agreements} />;
}
